import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { classNames } from '../helpers/functions';

import Badge from '../Badge';
import Tooltip from '../Tooltip';
import useAsync from '../../hooks/useAsync';
import { getAsset } from '../../api';
import { useParams } from 'react-router-dom';

const isTypeCreated = (type) => type === 'ticket.created';
const isTypeClosed = (type) =>
	type === 'ticket.closed' || type === 'ticket.done';
const isTypeUpdateSeverity = (type) => type === 'severity.updated';
const isTypeUpdateStatus = (type) => type === 'status.updated';
const isActive = (status) => status === 'incoming' || status === 'inprogress';

const getHealthArray = (item, maxDays) => {
	const dates = {};

	const addToDate = (date, value) => {
		if (dates[date]) {
			dates[date].push(value);
		} else {
			dates[date] = value ? [value] : null;
		}
	};

	let newStart = moment();
	let newEnd = moment().subtract(maxDays, 'days');

	while (newStart.isSameOrAfter(newEnd, 'day')) {
		addToDate(newEnd.format('MM/DD/YYYY'), null);

		newEnd.add(1, 'day');
	}

	item?.tickets?.map((ticket) => {
		const activities = (ticket?.activities || [])
			?.filter(
				(activity) =>
					activity?.type === 'ticket.created' ||
					activity?.type === 'ticket.closed' ||
					activity?.type === 'ticket.done' ||
					activity?.type === 'severity.updated' ||
					activity?.type === 'status.updated'
			)
			.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));

		let start = moment();
		let end = moment();

		activities?.map((currentActivity, i) => {
			const getData = (act) =>
				act.data_type === 'object' ? JSON.parse(act.data) : act.data;

			const nextActivity =
				activities.length >= i + 2 ? activities[i + 1] : null;
			let severity = ticket.severity;

			if (isTypeCreated(currentActivity.type)) {
				start = moment(currentActivity.createdAt);
				end = nextActivity
					? moment(nextActivity.createdAt).subtract(1, 'd')
					: moment();

				severity = nextActivity
					? isTypeUpdateSeverity(nextActivity.type)
						? getData(nextActivity).prev
						: severity
					: severity;
			}

			if (isTypeUpdateSeverity(currentActivity.type)) {
				start = moment(currentActivity.createdAt);

				if (nextActivity) {
					if (isTypeClosed(nextActivity.type)) {
						end = moment(nextActivity.createdAt);
					} else {
						end = moment(nextActivity.createdAt).subtract(1, 'd');
					}
				} else {
					end = moment();
				}

				severity =
					nextActivity && isTypeUpdateSeverity(nextActivity.type)
						? getData(nextActivity).prev
						: severity;
			}

			if (isTypeUpdateStatus(currentActivity.type)) {
				start = moment(currentActivity.createdAt);
				end = nextActivity
					? moment(currentActivity.createdAt).isSame(
							nextActivity.createdAt,
							'day'
					  )
						? moment(nextActivity.createdAt)
						: moment(nextActivity.createdAt).subtract(1, 'd')
					: moment();
			}

			while (start.isSameOrBefore(end, 'day')) {
				if (moment(start).isAfter(moment().subtract(maxDays, 'd'))) {
					/*addToDate(start.format('MM/DD/YYYY'), {
						id: ticket.id,
						severity,
						name: ticket?.subject || ticket?.ticket_code || ticket?.ticketCode,
					});*/
				}

				start.add(1, 'day');
			}
		});

		return;
	});

	return dates;
};

function getRange(startDate, numberOfDays) {
	let fromDate = moment(startDate).subtract(numberOfDays, 'days');
	let toDate = moment(startDate);
	let diff = toDate.diff(fromDate, 'days');

	let range = {};
	for (let i = 0; i <= diff; i++) {
		range[toDate.format('MM/DD/YYYY')] = [];
		toDate.subtract(1, 'day');
	}
	return range;
}

const OperationalChart = () => {
	const navigate = useNavigate();
	const breakpoint = {};
	const { id } = useParams();
	const { value: item } = useAsync(getAsset, { defaultPayload: { id } });

	const [maxDayOverview, setMaxDayOverview] = useState(
		breakpoint === 'md' ? 90 : 30
	);

	const healthBadgeData = item?.health
		? {
				operational: {
					status: 'success',
					label: 'Operational',
				},
				caution: {
					status: 'caution',
					label: 'Caution',
				},
				down: {
					status: 'error',
					label: 'Down',
				},
		  }[item?.health]
		: null;

	const dates = useMemo(() => {
		if (item) {
			return getHealthArray(item, maxDayOverview);
		}
	}, [item]);

	return (
		<div className="w-full relative">
			{healthBadgeData && (
				<div className="flex justify-start my-3">
					<Badge status={healthBadgeData.status} ghost>
						{healthBadgeData.label}
					</Badge>
				</div>
			)}
			<div
				className="grid gap-x-1 h-16"
				style={{
					gridTemplateColumns: `repeat(${maxDayOverview + 1}, minmax(0, 1fr))`,
				}}
			>
				{Object.keys({
					...getRange(moment().toDate(), maxDayOverview),
					...dates,
				})
					.reverse()
					.map((key) => {
						const label = moment(key).format('ddd, MMM Do, YYYY');
						const dailyTickets = (dates && dates[key]) || [];

						const breakdown = dailyTickets.reduce(
							(a, c) => {
								a[c.severity]++;
								return a;
							},
							{ minor: 0, major: 0, critical: 0 }
						);

						const daily = breakdown.critical
							? 'critical'
							: breakdown.major
							? 'major'
							: breakdown.minor
							? 'minor'
							: 'operational';

						const barStyle = {
							operational: { bar: 'bg-green-400/80 h-16', badge: 'success' },
							critical: { bar: 'bg-red-400/80 h-4 mt-12', badge: 'error' },
							major: { bar: 'bg-orange-400/80 h-8 mt-8', badge: 'warning' },
							minor: { bar: 'bg-yellow-400/80 h-12 mt-4', badge: 'caution' },
						}[daily];

						return (
							<Tooltip
								message={
									<div className="text-xxs">
										<div
											className={classNames(
												dailyTickets.length && 'border-b mb-2 pb-2'
											)}
										>
											{label}
										</div>
										{dailyTickets.map((ticket) => (
											<div
												className="cursor-pointer"
												onClick={() => navigate(`/tickets/${ticket.id}`)}
											>
												<Badge
													isBeta
													status={barStyle.badge}
												>{`${ticket.name}`}</Badge>
											</div>
										))}
									</div>
								}
							>
								<>
									<div
										className={classNames('rounded w-full mb-2', barStyle.bar)}
									></div>
								</>
							</Tooltip>
						);
					})}
			</div>

			<div className="relative my-3">
				<div className="absolute inset-0 flex items-center" aria-hidden="true">
					<div className="w-full border-t border-gray-300" />
				</div>
				<div className="relative flex justify-between">
					<span className="bg-white px-2 text-sm text-gray-500">{`${maxDayOverview} Days`}</span>
					<span className="bg-white px-2 text-sm text-gray-500">
						{`${(
							(1 - Object.keys(dates || {}).length / maxDayOverview) *
							100
						).toFixed(0)}% Uptime`}
					</span>
					<span className="bg-white px-2 text-sm text-gray-500">Today</span>
				</div>
			</div>
		</div>
	);
};

export default OperationalChart;
